import { Collapse, Dropdown, Modal, Popover, Tab, Tooltip } from 'bootstrap';

const helperMessageModalId = 'bootstrapHelperMessageModal';
const helperMessageModalQueue = [];
let helperMessageModalState = 'idle';

export function useBootstrap() {
    const safeQuerySelector = (selector, root = document) => {
        if (!selector || !root || typeof root.querySelector !== 'function') return null;

        try {
            return root.querySelector(selector);
        } catch (error) {
            return null;
        }
    };

    const getElement = (idOrElement) => {
        if (!idOrElement) return null;
        if (idOrElement.nodeType === 1) return idOrElement;
        if (typeof idOrElement !== 'string') return null;

        if (idOrElement.startsWith('#')) {
            return document.getElementById(idOrElement.slice(1));
        }

        return document.getElementById(idOrElement) || safeQuerySelector(idOrElement);
    };

    const getTriggerByTarget = (targetEl, toggleType) => {
        if (!targetEl || !targetEl.id) return null;

        return Array.from(document.querySelectorAll(`[data-bs-toggle="${toggleType}"]`)).find((toggleEl) => (
            toggleEl.getAttribute('data-bs-target') === `#${targetEl.id}`
            || toggleEl.getAttribute('href') === `#${targetEl.id}`
        )) || null;
    };

    const getTabToggleElement = (idOrElement) => {
        const element = getElement(idOrElement);
        if (!element) return null;

        if (element.matches('[data-bs-toggle="tab"]')) return element;
        return getTriggerByTarget(element, 'tab');
    };

    // BS5 `Collapse` gắn vào phần tử collapse (không phải nút toggle): nhận toggle thì đổi sang target của nó.
    const getCollapseElement = (idOrElement) => {
        const element = getElement(idOrElement);
        if (!element) return null;
        if (!element.matches('[data-bs-toggle="collapse"]')) return element;

        return getElement(element.getAttribute('data-bs-target') || element.getAttribute('href'));
    };

    const resetMessageModalElement = (modalEl) => {
        if (!modalEl) return;

        const messageEl = safeQuerySelector('.js-bootstrap-message', modalEl);
        const confirmButtonEl = safeQuerySelector('.js-bootstrap-confirm', modalEl);
        if (messageEl) {
            messageEl.innerHTML = '';
        }
        if (confirmButtonEl) {
            confirmButtonEl.textContent = '';
        }
    };

    const processMessageModalQueue = () => {
        if (helperMessageModalState !== 'idle' || !helperMessageModalQueue.length) return;

        const modalEl = ensureMessageModalElement();
        if (!modalEl) return;

        const nextMessage = helperMessageModalQueue.shift();
        const messageEl = safeQuerySelector('.js-bootstrap-message', modalEl);
        const confirmButtonEl = safeQuerySelector('.js-bootstrap-confirm', modalEl);
        if (!nextMessage || !messageEl || !confirmButtonEl) return;

        messageEl.innerHTML = nextMessage.message || '';
        confirmButtonEl.textContent = nextMessage.buttonLabel || '';

        helperMessageModalState = 'opening';
        openModal(modalEl);
    };

    const ensureMessageModalElement = () => {
        const existingModalEl = getElement(helperMessageModalId);
        if (existingModalEl) return existingModalEl;
        if (!document.body) return null;

        const modalEl = document.createElement('div');
        modalEl.id = helperMessageModalId;
        modalEl.className = 'modal fade';
        modalEl.tabIndex = -1;
        modalEl.setAttribute('role', 'dialog');
        modalEl.setAttribute('aria-hidden', 'true');
        modalEl.setAttribute('data-bs-backdrop', 'static');
        modalEl.setAttribute('data-bs-keyboard', 'false');
        modalEl.innerHTML = `
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-body js-bootstrap-message"></div>
                    <div class="modal-footer border-0 justify-content-center">
                        <button type="button" class="btn btn-dark js-bootstrap-confirm" data-bs-dismiss="modal"></button>
                    </div>
                </div>
            </div>
        `;

        modalEl.addEventListener('shown.bs.modal', () => {
            helperMessageModalState = 'open';
        });

        modalEl.addEventListener('hidden.bs.modal', () => {
            helperMessageModalState = 'idle';
            resetMessageModalElement(modalEl);
            setTimeout(() => {
                processMessageModalQueue();
            }, 0);
        });

        document.body.appendChild(modalEl);
        return modalEl;
    };

    const openModal = (idOrElement) => {
        const element = getElement(idOrElement);
        if (!element) return;

        Modal.getOrCreateInstance(element).show();
    };

    const closeModal = (idOrElement) => {
        const element = getElement(idOrElement);
        if (!element) return;

        Modal.getOrCreateInstance(element).hide();
    };

    // MVK-3385 — chạy `handler` mỗi khi modal BẮT ĐẦU mở. BS5 phát sự kiện DOM native ⇒ `addEventListener` bắt được.
    // Trả về hàm gỡ listener.
    const onModalShow = (idOrElement, handler) => {
        const element = getElement(idOrElement);
        if (!element) return () => {};

        const listener = () => handler();
        element.addEventListener('show.bs.modal', listener);

        return () => element.removeEventListener('show.bs.modal', listener);
    };

    // Modal đang mở HOẶC đang mở dở: trong lúc backdrop fade, `show.bs.modal` đã bắn mà class `show` chưa có,
    // còn cờ nội bộ `_isShown` của Bootstrap (BS5 vẫn giữ tên này) thì đã bật.
    const isModalShown = (idOrElement) => {
        const element = getElement(idOrElement);
        if (!element) return false;

        const instance = Modal.getInstance(element);
        return element.classList.contains('show') || Boolean(instance && instance._isShown);
    };

    const showDropdown = (idOrElement) => {
        const element = getElement(idOrElement);
        if (!element) return;

        Dropdown.getOrCreateInstance(element).show();
    };

    const hideDropdown = (idOrElement) => {
        const element = getElement(idOrElement);
        if (!element) return;

        Dropdown.getOrCreateInstance(element).hide();
    };

    const initTooltip = (idOrElement, options) => {
        const element = getElement(idOrElement);
        if (!element) return null;

        Tooltip.getOrCreateInstance(element, options || {});
        return element;
    };

    const initPopover = (idOrElement, options) => {
        const element = getElement(idOrElement);
        if (!element) return null;

        Popover.getOrCreateInstance(element, options || {});
        return element;
    };

    const showTab = (idOrElement) => {
        const element = getTabToggleElement(idOrElement);
        if (!element) return;

        Tab.getOrCreateInstance(element).show();
    };

    const showCollapse = (idOrElement) => {
        const element = getCollapseElement(idOrElement);
        if (!element) return;

        Collapse.getOrCreateInstance(element, { toggle: false }).show();
    };

    const hideCollapse = (idOrElement) => {
        const element = getCollapseElement(idOrElement);
        if (!element) return;

        Collapse.getOrCreateInstance(element, { toggle: false }).hide();
    };

    const openVideoModal = (code) => {
        const modalEl = getElement('videoModal');
        if (!modalEl) return;

        const modalBody = safeQuerySelector('.modal-body', modalEl);
        if (!modalBody) return;

        modalBody.innerHTML = '<div class="js-video youtube widescreen"><iframe width="729" height="500" src="//www.youtube.com/embed/' + code + '?hd=1" allowfullscreen></iframe></div>';
        openModal(modalEl);
    };

    const showModal = (message, buttonLabel) => {
        if (!document.body) return;

        helperMessageModalQueue.push({
            message,
            buttonLabel,
        });
        processMessageModalQueue();
    };

    return {
        closeModal,
        hideCollapse,
        hideDropdown,
        initPopover,
        initTooltip,
        isModalShown,
        onModalShow,
        openModal,
        openVideoModal,
        showCollapse,
        showDropdown,
        showModal,
        showTab,
    };
}
